/**
 * Git provider registry (git sync Stage 1, card 6 — owner: "leave space for
 * more git providers").
 *
 * Each entry declares who it is (id, label, icon), how one signs in
 * (`authKinds`: device / oauth / token / ssh), where it lives (`baseUrl`) and
 * what it can do (`abilities`: createRepo, listRepos, openInBrowser,
 * deviceFlow, tagWorkspace). Git sync and the UI ask the registry instead of
 * importing a provider: abilities decide what is offered, so a new host is one
 * entry here, not a branch in every caller.
 *
 * A workspace's git link names its provider (`git.provider`). Links written
 * before the registry existed carry none and are GitHub — `providerOf` reads
 * them that way, and `normalizeGitLink` stamps it on the next write.
 */
import github from './github';
import gitlab from './gitlab';
import gitea from './gitea';
import remote from './remote';
import local from './local';

export const DEFAULT_PROVIDER_ID = 'github';
export const LOCAL_PROVIDER_ID = 'local';

const REGISTRY = new Map([github, gitlab, gitea, remote, local].map((p) => [p.id, p]));

/**
 * The providers one signs in to (GitHub, GitLab, another server). "This
 * device" (./local.js) is registered — links name it — but is not a place
 * with an account, so it is not listed here.
 */
export function listProviders() {
  return [...REGISTRY.values()].filter((p) => !p.localOnly);
}

/** The provider with this id; throws on an unknown id (a link from a newer build). */
export function getProvider(id = DEFAULT_PROVIDER_ID) {
  const provider = REGISTRY.get(id || DEFAULT_PROVIDER_ID);
  if (!provider) {
    const err = new Error(`Unknown git provider "${id}"`);
    err.code = 'GIT_PROVIDER_UNKNOWN';
    throw err;
  }
  return provider;
}

/** The provider a workspace's git link points at (legacy links → GitHub). */
export function providerOf(workspaceOrGit) {
  const git = workspaceOrGit?.git !== undefined ? workspaceOrGit.git : workspaceOrGit;
  return getProvider(git?.provider || DEFAULT_PROVIDER_ID);
}

/** A link with its provider (and account, when known) written out. A local link has no account. */
export function normalizeGitLink(git, { accountId } = {}) {
  if (!git) return git;
  const out = { ...git, provider: git.provider || DEFAULT_PROVIDER_ID };
  if (out.provider === LOCAL_PROVIDER_ID) return out;
  if (accountId && !out.accountId) out.accountId = accountId;
  return out;
}

/** True when `provider` can do `ability` (false for anything it does not declare). */
export function can(provider, ability) {
  return Boolean(provider?.abilities?.[ability]);
}
